"use client"

import { HotelRoom } from "@/lib/redux/features/hotel/type"
import dayjs from "dayjs"
import { useSearchParams } from "next/navigation"
import { useMemo } from "react"
import { useTETranslation } from "../app/useTETranslation"
import { normalizeCancellationDeadline } from "@/utils/hotel/formatCancellationDatetime"
import { useHotelMarkupPolicy } from "./useHotelMarkupPolicy"

export const useHotelCancellationPolicies = (room?: HotelRoom) => {
  const { getFormatCurrency, getValidDayjs } = useTETranslation()
  const searchParams = useSearchParams()
  const { getPerNightPrice, getTotalPayment } = useHotelMarkupPolicy()

  const optionPrice = room ? getPerNightPrice(room) : 0

  return useMemo(() => {
    if (!room?.cancellationPolicy?.date?.length) {
      return []
    }

    return room.cancellationPolicy.date
      .reduce((arr: any[], item) => {
        const { before } = item
        const dayjsBefore = getValidDayjs(before)
        const now = dayjs()
        if (!before || dayjsBefore.diff(now, "day") < 0) {
          return arr
        }

        const checkIn = getValidDayjs(searchParams.get("in"))
        const days = checkIn.diff(dayjsBefore, "days")
        const parameter = room.cancellationPolicy.parameter?.find(p => p.days <= days)
        let fee = 0
        if (parameter?.charge === "N") {
          fee = parameter.value * (
            room.dailyRate[0].exclusive + room.dailyRate[0].tax + room.dailyRate[0].fees
          )
        } else if (parameter?.charge === "P") {
          fee = parameter.value * getTotalPayment(room) / 100
        }

        fee = Math.round(fee)
        if (fee >= optionPrice) {
          return arr
        }

        const deadline = normalizeCancellationDeadline(getValidDayjs(before))
        const deadlineLabel = deadline.format("HH:mm DD MMMM")

        const text = fee <= 0
          ? `Hủy miễn phí trước ${deadlineLabel}`
          : fee >= optionPrice
            ? "Không hoàn tiền"
            : `Phí hủy ${getFormatCurrency(fee)} trước ${deadlineLabel}`

        const textEn = fee <= 0
          ? `Free cancellation before ${deadlineLabel}`
          : fee >= optionPrice
            ? "Non-refundable"
            : `Cancellation fee ${getFormatCurrency(fee)} before ${deadlineLabel}`

        return arr.concat([{ ...item, text, textEn, fee }])
      }, [])
      .filter((item) => item.fee > 0 || getValidDayjs(room.freeCancellationDate).diff(dayjs(), "hour") < 1)
  }, [
    room,
    optionPrice,
    searchParams,
    getValidDayjs,
    getFormatCurrency,
    getTotalPayment,
  ])
}
